/**
 * @fileoverview Authentication Session Hook
 * 
 * Provides the single source of truth for authentication session data.
 * All other auth-related hooks should derive their data from this hook
 * to ensure consistency and prevent redundant API calls.
 * 
 * @module hooks/useAuthSession
 * @version 1.0.0
 * @since 2024-01-01
 * 
 * @architecture
 * ```
 * useAuthSession (this hook) ← Single source of truth
 *     ↓
 * ├── useAuth         ← Derives user from session
 * ├── useCurrentUser  ← Derives profile from session
 * └── useUserRole     ← Derives role from session
 * ```
 * 
 * @caching
 * - staleTime: 30 seconds - Data considered fresh
 * - gcTime: 5 minutes - Cache retention after unmount
 * - refetchOnWindowFocus: true - Revalidate on tab switch
 * - refetchOnMount: true - Always check auth on mount
 * - refetchOnReconnect: true - Revalidate after network recovery
 * 
 * @security
 * - Session data is fetched from Supabase auth service
 * - JWT tokens are automatically refreshed by Supabase client
 * - Session includes user ID, email, and metadata
 * 
 * @example
 * // Basic usage - check if authenticated
 * const { data: session, isLoading } = useAuthSession();
 * 
 * if (isLoading) return <Spinner />;
 * if (!session) return <Navigate to="/login" />;
 * 
 * // Access user data
 * console.log(session.user.email);
 * console.log(session.user.id);
 * 
 * @example
 * // In protected route wrapper
 * function ProtectedRoute({ children }) {
 *   const { data: session, isLoading } = useAuthSession();
 *   
 *   if (isLoading) return <LoadingScreen />;
 *   if (!session) return <Navigate to="/login" replace />;
 *   
 *   return children;
 * }
 * 
 * @see {@link useAuth} - For authentication actions (login, logout)
 * @see {@link useCurrentUser} - For extended profile data
 * @see {@link queryKeys.auth.session} - Query key used for caching
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/query-keys";
import type { Session } from "@supabase/supabase-js";

/**
 * Single source of truth for authentication session data.
 * 
 * This hook provides the foundation for all authentication state in the app.
 * It fetches and caches the current Supabase session, automatically handling
 * token refresh and session validation.
 * 
 * @returns {UseQueryResult<Session | null>} React Query result object
 * @returns {Session | null} returns.data - The current session or null if not authenticated
 * @returns {boolean} returns.isLoading - Whether the session is being fetched
 * @returns {boolean} returns.isError - Whether an error occurred
 * @returns {Error | null} returns.error - The error object if isError is true
 * @returns {boolean} returns.isFetching - Whether a background refetch is in progress
 * @returns {boolean} returns.isStale - Whether the cached data is stale
 * 
 * @example
 * // Simple auth check
 * const { data: session } = useAuthSession();
 * const isAuthenticated = !!session;
 * 
 * @example
 * // With loading state
 * const { data: session, isLoading, error } = useAuthSession();
 * 
 * if (isLoading) return <Skeleton />;
 * if (error) return <ErrorMessage error={error} />;
 * if (!session) return <LoginPrompt />;
 * 
 * return <AuthenticatedContent user={session.user} />;
 * 
 * @throws {Error} If Supabase client is not properly initialized
 * 
 * @sideEffects
 * - Subscribes to Supabase auth state changes (handled by parent auth context)
 * - May trigger token refresh if JWT is expired
 */
export function useAuthSession() {
  return useQuery({
    queryKey: queryKeys.auth.session(),
    
    /**
     * Fetches the current authentication session from Supabase.
     * Returns null if no active session exists.
     * 
     * @returns {Promise<Session | null>} The current session or null
     */
    queryFn: async (): Promise<Session | null> => {
      const start = performance.now();
      const { data: { session } } = await supabase.auth.getSession();
      const duration = performance.now() - start;
      
      // Log slow auth calls for debugging
      if (duration > 500) {
        console.warn(`[Perf] Auth getSession took ${duration.toFixed(0)}ms`);
      }
      
      return session;
    },
    
    // ─────────────────────────────────────────────────────────────
    // Cache Configuration
    // ─────────────────────────────────────────────────────────────
    
    /** Data is considered fresh for 30 seconds */
    staleTime: 30 * 1000,
    
    /** Cache is retained for 5 minutes after all subscribers unmount */
    gcTime: 5 * 60 * 1000,
    
    // ─────────────────────────────────────────────────────────────
    // Refetch Triggers
    // ─────────────────────────────────────────────────────────────
    
    /** Refetch when window regains focus (e.g., tab switch) */
    refetchOnWindowFocus: true,
    
    /** Always check auth status when component mounts */
    refetchOnMount: true,
    
    /** Refetch if network connection is restored */
    refetchOnReconnect: true,
  });
}
